'use client';

import { useState, useEffect, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';

import { loginSchema, type LoginInput } from '@hr/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Loader2,
  Shield,
  ArrowLeft,
  Separator,
  useToast,
} from '@hr/ui';
import { useAuthStore, type UserProfile, type TenantContext } from '@/lib/stores/auth-store';
import { api, isMfaRequired, type SsoProviderInfo } from '@/lib/api';

// Helper to fetch user profile and complete auth setup
// Returns true if onboarding is incomplete (caller should redirect to /setup)
async function completeAuthSetup(
  accessToken: string,
  setAuth: (params: {
    accessToken: string;
    user: UserProfile;
    tenant: TenantContext;
    availableTenants?: TenantContext[];
  }) => void
): Promise<{ onboardingCompleted: boolean }> {
  // Store token temporarily for API calls
  if (typeof window !== 'undefined') {
    localStorage.setItem('accessToken', accessToken);
  }

  // Fetch user profile with tenant memberships
  const response = await api.auth.me() as {
    user: {
      id: string;
      email: string;
      mfaEnabled: boolean;
      emailVerified: boolean;
      isPlatformAdmin?: boolean;
    };
    membership: {
      id: string;
      role: string;
      tenantId: string;
      tenantName: string;
      tenantSlug: string;
      employeeId: string | null;
      onboardingCompleted: boolean;
    };
    tenants: Array<{
      id: string;
      name: string;
      slug: string;
      role: string;
    }>;
  };

  // Build user profile
  const user: UserProfile = {
    id: response.user.id,
    email: response.user.email,
    mfaEnabled: response.user.mfaEnabled,
    emailVerified: response.user.emailVerified,
    isPlatformAdmin: response.user.isPlatformAdmin ?? false,
  };

  // Build tenant contexts from tenants array
  const availableTenants: TenantContext[] = (response.tenants || []).map((tenant) => ({
    id: tenant.id,
    slug: tenant.slug,
    name: tenant.name,
    role: tenant.role as TenantContext['role'],
    employeeId: null, // Not provided in this response
  }));

  const onboardingCompleted = response.membership?.onboardingCompleted ?? true;

  // Use current membership tenant as current tenant
  const currentTenant: TenantContext = {
    id: response.membership.tenantId,
    slug: response.membership.tenantSlug,
    name: response.membership.tenantName,
    role: response.membership.role as TenantContext['role'],
    employeeId: response.membership.employeeId ?? null,
    onboardingCompleted,
  };

  if (!currentTenant) {
    throw new Error('No tenant membership found for this user');
  }

  // Set auth state
  setAuth({
    accessToken,
    user,
    tenant: currentTenant,
    availableTenants,
  });

  return { onboardingCompleted };
}

function LoginPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [isLoading, setIsLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [isVerifyingMfa, setIsVerifyingMfa] = useState(false);
  const [ssoProviders, setSsoProviders] = useState<SsoProviderInfo[]>([]);
  const [loadingSso, setLoadingSso] = useState(false);
  const [tenantSlug, setTenantSlug] = useState<string>('');

  // Check for session expired or SSO error from callback
  useEffect(() => {
    const expired = searchParams.get('expired');
    if (expired) {
      toast({
        title: 'Session expired',
        description: 'You were logged out for security. Please sign in again.',
      });
    }
    const error = searchParams.get('error');
    if (error) {
      toast({
        variant: 'destructive',
        title: 'SSO Login failed',
        description: decodeURIComponent(error),
      });
    }
  }, [searchParams, toast]);

  // Fetch SSO providers when tenant slug changes
  useEffect(() => {
    if (!tenantSlug) {
      setSsoProviders([]);
      return;
    }

    const fetchProviders = async () => {
      try {
        const providers = await api.sso.getProviders(tenantSlug);
        setSsoProviders(providers);
      } catch {
        // Silently fail - tenant may not have SSO configured
        setSsoProviders([]);
      }
    };

    // Debounce the fetch
    const timeoutId = setTimeout(fetchProviders, 500);
    return () => clearTimeout(timeoutId);
  }, [tenantSlug]);

  const handleSsoLogin = (provider: SsoProviderInfo) => {
    setLoadingSso(true);
    // Redirect to SSO login URL
    window.location.href = api.sso.getLoginUrl(provider.id, '/dashboard');
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginInput) => {
    setIsLoading(true);
    try {
      const response = await api.auth.login(data);

      // Check if MFA is required
      if (isMfaRequired(response)) {
        setMfaRequired(true);
        setTempToken(response.tempToken);
        toast({
          title: 'Verification required',
          description: 'Please enter your two-factor authentication code.',
        });
      } else {
        // Complete auth setup by fetching user profile
        const { onboardingCompleted } = await completeAuthSetup(response.accessToken, setAuth);
        toast({
          title: 'Welcome back!',
          description: 'You have been logged in successfully.',
        });
        // Redirect to setup if onboarding is incomplete, otherwise dashboard
        window.location.href = onboardingCompleted ? '/dashboard' : '/setup';
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Login failed',
        description: error instanceof Error ? error.message : 'Invalid credentials',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tempToken || mfaCode.length !== 6) {
      toast({
        variant: 'destructive',
        title: 'Invalid code',
        description: 'Please enter a 6-digit verification code',
      });
      return;
    }

    setIsVerifyingMfa(true);
    try {
      const response = await api.auth.completeMfaLogin(tempToken, mfaCode);
      // Complete auth setup by fetching user profile
      const { onboardingCompleted } = await completeAuthSetup(response.accessToken, setAuth);
      toast({
        title: 'Welcome back!',
        description: 'You have been logged in successfully.',
      });
      // Redirect to setup if onboarding is incomplete, otherwise dashboard
      window.location.href = onboardingCompleted ? '/dashboard' : '/setup';
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Verification failed',
        description: error instanceof Error ? error.message : 'Invalid code. Please try again.',
      });
    } finally {
      setIsVerifyingMfa(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaRequired(false);
    setTempToken(null);
    setMfaCode('');
  };

  // MFA verification screen
  if (mfaRequired) {
    return (
      <Card>
        <CardHeader className="space-y-1">
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <CardTitle className="text-2xl">Two-Factor Authentication</CardTitle>
          </div>
          <CardDescription>
            Enter the 6-digit code from your authenticator app
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleMfaSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mfaCode">Verification Code</Label>
              <Input
                id="mfaCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="123456"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoComplete="one-time-code"
                autoFocus
              />
              <p className="text-sm text-muted-foreground">
                Open your authenticator app and enter the code for this account
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full"
              disabled={isVerifyingMfa || mfaCode.length !== 6}
            >
              {isVerifyingMfa && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Verify
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={handleBackToLogin}
              disabled={isVerifyingMfa}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to login
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  // Standard login screen
  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl">Sign in</CardTitle>
        <CardDescription>Enter your email and password to access your account</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@company.com"
              {...register('email')}
              onChange={(e) => {
                register('email').onChange(e);
                // Extract tenant from email domain for SSO lookup
                const emailValue = e.target.value;
                const domain = emailValue.split('@')[1];
                if (domain) {
                  // Use domain as tenant slug (simplified - in production you'd have a lookup)
                  const slug = domain.split('.')[0];
                  if (slug) {
                    setTenantSlug(slug);
                  }
                }
              }}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link
                href="/forgot-password"
                className="text-sm text-primary hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <Input id="password" type="password" {...register('password')} />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sign in
          </Button>

          {/* SSO Providers Section */}
          {ssoProviders.length > 0 && (
            <>
              <div className="relative w-full">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>
              <div className="grid gap-2 w-full">
                {ssoProviders.map((provider) => (
                  <Button
                    key={provider.id}
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => handleSsoLogin(provider)}
                    disabled={loadingSso}
                  >
                    {loadingSso ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Sign in with {provider.name}
                  </Button>
                ))}
              </div>
            </>
          )}

          {/* SSO Discovery - Enter company domain */}
          {ssoProviders.length === 0 && (
            <div className="w-full">
              <details className="group">
                <summary className="cursor-pointer text-sm text-center text-muted-foreground hover:text-foreground transition-colors">
                  Sign in with SSO
                </summary>
                <div className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="tenantSlug">Company Identifier</Label>
                    <Input
                      id="tenantSlug"
                      type="text"
                      placeholder="your-company"
                      value={tenantSlug}
                      onChange={(e) => setTenantSlug(e.target.value.toLowerCase())}
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter your company identifier to discover SSO options
                    </p>
                  </div>
                </div>
              </details>
            </div>
          )}

          <p className="text-center text-sm text-muted-foreground">
            Don&apos;t have an account?{' '}
            <Link href="/register" className="text-primary hover:underline">
              Create one
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
      <LoginPageContent />
    </Suspense>
  );
}
